import type { CSSProperties } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { SnapmonLogo } from './SnapmonLogo';
import { Icon } from './Icons';
import { useAuthStore } from '../auth/store';

interface SidebarProps {
  /** Called after a nav link is tapped — lets the mobile drawer close. */
  onNavigate?: () => void;
  /** Overrides merged onto the root <aside> (drawer mode needs static
   *  position + full height inside its fixed wrapper). */
  styleOverride?: CSSProperties;
}

export function Sidebar({ onNavigate, styleOverride }: SidebarProps = {}) {
  const admin = useAuthStore((s) => s.admin);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  const items = [
    { to: '/', label: 'Dashboard', Icon: Icon.Home, end: true },
    { to: '/creators', label: 'Creators', Icon: Icon.Users, end: false },
    { to: '/codes', label: 'Code requests', Icon: Icon.Plus, end: false },
    { to: '/feature-requests', label: 'Feature requests', Icon: Icon.Bulb, end: false },
    { to: '/surveys', label: 'Surveys', Icon: Icon.Check, end: false },
    { to: '/payouts', label: 'Payouts', Icon: Icon.Money, end: false },
    { to: '/activity', label: 'Activity', Icon: Icon.Bell, end: false },
    { to: '/app-version', label: 'App Version', Icon: Icon.Settings, end: false },
  ];

  const logout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <aside
      style={{
        borderRight: '1px solid var(--line)',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 14px',
        gap: 6,
        position: 'sticky',
        top: 0,
        height: '100dvh',
        ...styleOverride,
      }}
      className="snapmon-sidebar"
    >
      <div style={{ padding: '6px 8px 18px' }}>
        <SnapmonLogo />
      </div>
      <div
        style={{
          padding: '0 8px 6px',
          fontSize: 10,
          color: 'var(--ink-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontWeight: 600,
        }}
      >
        Internal admin
      </div>
      {items.map(({ to, label, Icon: I, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          style={({ isActive }) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 10px',
            borderRadius: 'var(--r-md)',
            background: isActive ? 'var(--brand-soft)' : 'transparent',
            color: isActive ? 'var(--brand-ink)' : 'var(--ink-2)',
            fontWeight: isActive ? 600 : 500,
            fontSize: 13.5,
            textDecoration: 'none',
            transition: 'background 0.14s ease',
          })}
        >
          <I />
          {label}
        </NavLink>
      ))}

      <div style={{ marginTop: 'auto' }}>
        {admin && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'var(--brand)',
                color: 'var(--bg-elev)',
                display: 'grid',
                placeItems: 'center',
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 13,
                textTransform: 'uppercase',
              }}
            >
              {admin.username.slice(0, 2)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {admin.username}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                staff · admin
              </div>
            </div>
            <button
              onClick={logout}
              title="Sign out"
              style={{
                display: 'inline-grid',
                placeItems: 'center',
                width: 28,
                height: 28,
                borderRadius: 'var(--r-sm)',
                color: 'var(--ink-3)',
                flexShrink: 0,
              }}
            >
              <Icon.Logout />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
